// Cache utilities for storing and retrieving data

interface CacheItem<T> {
  value: T;
  timestamp: number;
}

export class Cache<T> {
  private cache: Map<string, CacheItem<T>>;
  private ttl: number;

  constructor(ttl: number = 5 * 60 * 1000) {
    // 5 minutes default TTL
    this.cache = new Map();
    this.ttl = ttl;
  }

  set(key: string, value: T): void {
    this.cache.set(key, { value, timestamp: Date.now() });
  }

  get(key: string): T | null {
    const item = this.cache.get(key);
    if (!item) return null;

    if (Date.now() - item.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }

    return item.value;
  }

  clear(): void {
    this.cache.clear();
  }
}

// Specific cache instances
export const calculationCache = new Cache<unknown>();
export const validationCache = new Cache<unknown>();

// State persistence utilities
const STORAGE_KEY = "traveller_ship_builder_state";

export const loadState = (): Partial<{
  shipBuilder: unknown;
  optionalSystems: unknown;
  breakawayHull: unknown;
  module: unknown;
  ui: unknown;
}> => {
  try {
    const serializedState = localStorage.getItem(STORAGE_KEY);
    if (serializedState === null) {
      return {};
    }
    return JSON.parse(serializedState);
  } catch (error) {
    console.error("Failed to load state from localStorage:", error);
    return {};
  }
};

export const saveState = (state: {
  shipBuilder: unknown;
  optionalSystems: unknown;
  breakawayHull: unknown;
  module: unknown;
  ui: unknown;
}): void => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(STORAGE_KEY, serializedState);
  } catch (error) {
    console.error("Failed to save state to localStorage:", error);
  }
};

export const clearState = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Failed to clear state from localStorage:", error);
  }
};
